import { Edit2, Plus, QrCode, Save, X } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { useMoldQRCode } from '../../hooks/useMoldQRCode';
import { useState } from 'react';
import QRCodeErrorModal from '../shared/QRCodeErrorModal';

interface ActionButtonsProps {
  isEditing: boolean;
  isLoading: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  moldId?: string; // Add optional moldId prop
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  isEditing,
  isLoading,
  onEdit,
  onSave,
  onCancel,
  moldId, // Add moldId to props
}) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>(); // Get ID from URL if not passed as prop
  const { downloadMoldQRCode, isLoading: qrLoading, error: qrError, clearError } = useMoldQRCode();
  const [showErrorModal, setShowErrorModal] = useState(false);

  const handleCreateOrder = () => {
    const selectedMoldId = moldId || id; // Use passed moldId or fallback to URL param
    navigate(`${ROUTES.CREATE_MOLD_ORDERS_MENU}?selected=${selectedMoldId}`);
  };

  const handleQRCodeDownload = async () => {
    try {
      const selectedMoldId = moldId || id;
      if (selectedMoldId) {
        await downloadMoldQRCode(selectedMoldId);
      }
    } catch (err) {
      setShowErrorModal(true);
    }
  };

  if (isEditing) {
    return (
      <>
        <button
          onClick={onSave}
          disabled={isLoading}
          className="w-full h-12 inline-flex items-center justify-center px-6 bg-blue-600 text-white rounded-full shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-lg font-medium disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-2 h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              กำลังบันทึก...
            </>
          ) : (
            <>
              <Save size={20} className="mr-2" />
              บันทึก
            </>
          )}
        </button>
        <button
          onClick={onCancel}
          className="w-full h-12 inline-flex items-center justify-center px-6 border-2 border-gray-300 text-gray-700 rounded-full shadow-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-lg font-medium"
        >
          <X size={20} className="mr-2" />
          ยกเลิก
        </button>
      </>
    );
  }

  return (
    <>
      <button
        onClick={handleCreateOrder}
        className="w-full h-12 inline-flex items-center justify-center px-6 bg-blue-600 text-white rounded-full shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-lg font-medium"
      >
        <Plus size={20} className="mr-2" />
        สร้างใบงาน
      </button>
      <button
        onClick={handleQRCodeDownload}
        disabled={qrLoading}
        className={`w-full h-12 inline-flex items-center justify-center px-6 border-2 border-gray-300 text-gray-700 rounded-full shadow-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-lg font-medium ${qrLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <QrCode size={20} className="mr-2" />
        {qrLoading ? 'กำลังดาวน์โหลด...' : 'QR Code'}
      </button>
      <button
        onClick={onEdit}
        className="w-full h-12 inline-flex items-center justify-center px-6 border-2 border-gray-300 text-gray-700 rounded-full shadow-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-lg font-medium"
      >
        <Edit2 size={20} className="mr-2" />
        แก้ไขข้อมูล
      </button>
      <QRCodeErrorModal
        isOpen={showErrorModal}
        error={qrError}
        onClose={() => {
          setShowErrorModal(false);
          clearError();
        }}
      />
    </>
  );
};

export default ActionButtons;
